import React, { memo, useCallback, useMemo } from 'react';
import PhoneIcon from '@material-ui/icons/Phone';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { BlockadeTypeId, ExtendedExaminersList } from '../ExaminersStructure.types';
import { useStyles } from './ExaminersStructureElement.styles';
import { ExaminerFunction } from '../../../pages/Assessment/Assessment.types';
import useSessionUser from '../../../hooks/useSessionUser';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import ManualLockedPersonIcon from '@mui/icons-material/LockPersonOutlined';
import AutoLockedPersonIcon from '../../../assets/icons/auto-locked.svg';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import { formatPhone } from '../../UserProfile/helpers';
import { useExaminersTreeContext } from '../ExaminersTreeContext';
import ExaminersStructure from '../ExaminersStructure.component';
import { isExaminingTeamBlocked } from '../helpers';

interface SidebarListProps {
  examiner?: ExtendedExaminersList | null;
  isFirst?: boolean;
}

const ExaminersStructureElement: React.FC<SidebarListProps> = ({ examiner, isFirst }) => {
  const classes = useStyles();
  const { displayName, isCurrentUser, isHighlightText, isActive, isClosed } = examiner || {};
  const { getMenuItems, getMenuItemsConditions, onActiveElementChange, onClosedElementChange } =
    useExaminersTreeContext();

  const { isUserCZE } = useSessionUser();

  const isMenuItemPZE = examiner?.function === ExaminerFunction.PZE;
  const isMenuItemCZE = examiner?.function === ExaminerFunction.CZE;

  const isDeskAssignedExaminer = examiner?.examiner;
  const menuItemsConditions = useMemo(
    () => (examiner ? getMenuItemsConditions(examiner) : null),
    [examiner, getMenuItemsConditions],
  );

  const onSetActive = useCallback((): void => {
    if (examiner) {
      onActiveElementChange(examiner);
    }
  }, [examiner, onActiveElementChange]);

  const isTeamBlocked = useCallback(() => (examiner && isExaminingTeamBlocked(examiner)) ?? false, [examiner]);

  const examinerActionsMenuIcon = useMemo((): JSX.Element | string => {
    if (!displayName || !examiner) {
      return <> </>;
    }

    if (menuItemsConditions && Object.values(menuItemsConditions).some((item) => !!item)) {
      return (
        <ActionsMenu
          isNotInTable={true}
          menuItems={() => getMenuItems(examiner, menuItemsConditions, !!isTeamBlocked())}
          iconStyle={{ color: '#FFFFFF', position: 'absolute', top: '1px', left: 'calc(100% - 25px)' }}
        />
      );
    } else {
      return <> </>;
    }
  }, [displayName, examiner, getMenuItems, isTeamBlocked, menuItemsConditions]);

  if (!examiner) {
    return null;
  }

  const handleArrowClick = (isClosed: boolean) => {
    onClosedElementChange(examiner, isClosed);
  };

  const highlightText = (text: string): React.ReactElement => {
    return <span className={classes.usersListElementHighlighted}>{text}</span>;
  };

  const getDisplayText = (text: string, needToHighlightText?: boolean): React.ReactElement | string => {
    if (isDeskAssignedExaminer && needToHighlightText) {
      if (displayName && examiner.isHighlightText) {
        return <>{highlightText(isDeskAssignedExaminer.displayName)}</>;
      }
      return ` ${text}`;
    } else return ` ${text}`;
  };
  return (
    <>
      <TooltipOne
        placement={'right-end'}
        classes={{
          tooltip: classes.userListElementTooltip,
        }}
        title={
          <div>
            <p className={classes.userListElementTooltipText}>{examiner.tooltipText}</p>
            {isDeskAssignedExaminer && (
              <p className={classes.userListElementTooltipText}>
                <PhoneIcon
                  classes={{
                    root: classes.usersListElementContentIcon,
                  }}
                />
                {isDeskAssignedExaminer.phoneNumber
                  ? formatPhone(isDeskAssignedExaminer.phoneNumber)
                  : 'Numer telefonu ukryty'}
              </p>
            )}
          </div>
        }
      >
        <li
          className={[
            classes.usersListElement,
            isFirst ? classes.usersListElementFirst : '',
            !isClosed && examiner && examiner.inferiors.length ? classes.usersListElementOpened : '',
          ].join(' ')}
        >
          {!isFirst && <div className={classes.usersListElementLine} />}
          <div
            onClick={!isFirst ? onSetActive : undefined}
            className={
              isActive
                ? [classes.usersListElementContent, classes.usersListElementContentActive].join(' ')
                : classes.usersListElementContent
            }
          >
            <div
              className={[classes.usersListElementText, isCurrentUser ? classes.usersListElementTextActive : ''].join(
                ' ',
              )}
              style={{
                maxWidth:
                  examiner && examiner.examiner?.examinerPermission?.isBlocked && (isMenuItemCZE || isMenuItemPZE)
                    ? '80px'
                    : '100px',
              }}
            >
              {examiner && examiner.examiner?.isOnline ? (
                <TooltipOne title="Aktywny" placement="top">
                  <CheckCircleIcon
                    color={'success'}
                    className={classes.loginIcon}
                    sx={{ width: '15px', height: '15px' }}
                  />
                </TooltipOne>
              ) : (
                <TooltipOne title="Nieaktywny" placement="top">
                  <DoDisturbOnIcon
                    color={'error'}
                    className={classes.loginIcon}
                    sx={{ width: '15px', height: '15px' }}
                  />
                </TooltipOne>
              )}

              {displayName && getDisplayText(displayName, isHighlightText)}
              {examinerActionsMenuIcon}
            </div>

            {examiner &&
              examiner.inferiors.length > 0 &&
              !isUserCZE &&
              (!isClosed ? (
                <ArrowDropUpIcon
                  style={{ transform: 'translate(-18px, 0px)' }}
                  data-testid="arrow_up"
                  onClick={() => handleArrowClick(true)}
                />
              ) : (
                <ArrowDropDownIcon
                  style={{ transform: 'translate(-18px, 0px)' }}
                  data-testid="arrow_down"
                  onClick={() => handleArrowClick(false)}
                />
              ))}
            {examiner.blockadeTypeId === BlockadeTypeId.Manual && (
              <TooltipOne title="Zablokowany ręcznie" placement="top">
                <ManualLockedPersonIcon className={classes.lockIcon} />
              </TooltipOne>
            )}
            {examiner.blockadeTypeId === BlockadeTypeId.Automatic && (
              <TooltipOne title="Zablokowany automatycznie" placement="top">
                <img
                  src={AutoLockedPersonIcon}
                  className={classes.autolockIcon}
                  alt="ikona egzaminator zablokowany automatycznie"
                />
              </TooltipOne>
            )}
          </div>
        </li>
      </TooltipOne>
      {!isClosed && (
        <ul className={classes.usersListUl}>
          {examiner.inferiors.map((e) => (
            <ExaminersStructure element={e} key={e.id} />
          ))}
        </ul>
      )}
    </>
  );
};

export default memo(ExaminersStructureElement);
